// @flow
import React, { useState, useCallback, useEffect, useMemo } from "react";
import Modal, { ModalSize } from "components/Modal";
import { createModal } from "react-modal-promise";
import styles from "./PickerModal.module.css";
import MiniPicker from "views/minipicker";
import SearchPicker from "./SearchPicker";
import UrlPicker from "./UrlPicker";
import Button from "react-bootstrap/lib/Button";
import SplitButton from "react-bootstrap/lib/SplitButton";
import MenuItem from "react-bootstrap/lib/MenuItem";
import loc from "i18next";
import HelpBlock from "react-bootstrap/lib/HelpBlock";
import { fetchBreadcrumb } from "data/api";
import { useModalPostMessageHandler, useAbortController } from "hooks";
import { sitesSelector } from "data/storeHelper";
import { RenderType } from "./RenderType";
import {
    PickerType,
    type PickerTypeEnum,
    type PickerActionDTO,
    PickerRenderType,
    type SearchValueTypeEnum,
    SearchValueType,
} from "data/types";

type Props = {
    /** react-modal-promise: whether to show dialog */
    open: boolean,
    /** react-modal-promise: callback when user closes Dialog */
    close: () => void,
    /** optional search form */
    searchForm: number,
    /** current folder's itemUri  */
    itemUri: string,
    /** type of picker
     * @default {PickerTypeEnum} PickerType.File
     */
    pickerType: PickerTypeEnum,
    /** optional help text to display the user */
    helpText?: string,
    /** callback for additional actions */
    onSelect: (eventKey) => void,
    /** additional actions */
    additionalActions?: Array<PickerActionDTO>,
    /** optional toggles */
    toggles?: Array<string>,
    /** optional breadcrumb scoping
     * @default {boolean} false
     */
    scopeBreadcrumb?: boolean,
    /** can change site
     * @default {boolean} false
     */
    canChangeSite?: boolean,
    /** can select site
     * @default {boolean} false
     */
    canSelectSite?: boolean,
    /** optional selectButton string
     * @default {string} minipicker:selectButton
     */
    selectButton?: string,
    /** default render type
     * @default {PickerRenderTypeEnum} PickerRenderType.Target
     */
    defaultRenderType?: PickerRenderTypeEnum,
    /** allowed render types
     * @default {PickerRenderTypeEnum} PickerRenderType.All
     */
    allowedRenderTypes?: PickerRenderTypeEnum,
    /** optional filters */
    filters?: Array<FilterDTO>,
    /** return item
     * @default {boolean} false
     */
    returnItem?: boolean,
};

/** allows user to pick a Folder or Document */
export const PickerModal = ({
    open,
    close,
    searchForm = -1,
    itemUri,
    pickerType = PickerType.File,
    helpText,
    onSelect,
    additionalActions,
    toggles,
    title,
    caption = loc.t("plugin:extraAttributes.link_open"),
    icon = "fa-regular fa-arrow-up-right-from-square",
    isIconLast = true,
    scopeBreadcrumb = false,
    canChangeSite = false,
    canSelectSite = false,
    selectButton = loc.t("minipicker:selectButton"),
    defaultRenderType = PickerRenderType.Target,
    allowedRenderTypes = PickerRenderType.All,
    filters: filtersFromProps = [],
    returnItem = false,
}: Props) => {
    // remember the currently shown folder itemUri
    const [currentRootItemUri, setCurrentRootItemUri] = useState(itemUri);
    // remember the currently selected itemUri (folder/doc)
    const [currentItemUri, setCurrentItemUri] = useState(null);
    const [currentItem, setCurrentItem] = useState({});
    const [currentRootItemUriLevels, setCurrentItemUriLevels] = useState(-1);
    const sites = useMemo(sitesSelector, []);
    // set itemType filter based on pickerType
    const filters = useMemo(() => {
        if (pickerType === PickerType.Any) {
            return filtersFromProps;
        }
        return [
            ...filtersFromProps.filter((f) => f.name !== "ItemType"),
            {
                name: "ItemType",
                valueType: SearchValueType.ItemType,
                value: pickerType === PickerType.Folder ? "Folder" : "Document",
            },
        ];
    }, [filtersFromProps, pickerType]);

    /* istanbul ignore next*/
    useEffect(() => {
        canSelectSite &&
            scopeBreadcrumb &&
            console.warn(
                "Site Selection disabled: Prop canSelectSite will be false, when scopeBreadcrumb is true!"
            );
    }, [canSelectSite, scopeBreadcrumb]);

    const handleFolderSelect = useCallback(
        (itemUri, treeNodeId) => {
            if (pickerType === PickerType.File) {
                return;
            }
            setCurrentItem((item) => ({ ...item, itemUri }));
            setCurrentItemUri(itemUri);
        },
        [pickerType]
    );
    const handleDocumentSelect = useCallback(
        (e, itemUri, index) => {
            if (pickerType === PickerType.Folder) {
                return;
            }
            setCurrentItem((item) => ({ ...item, itemUri }));
            setCurrentItemUri(itemUri);
        },
        [pickerType]
    );
    const setRootItemUri = useCallback(
        (itemUri: string) => {
            setCurrentRootItemUri(itemUri);
            if (pickerType !== PickerType.File) {
                handleFolderSelect(
                    canSelectSite || !sites.some((s) => s.itemUri === itemUri)
                        ? itemUri
                        : null
                );
            } else {
                setCurrentItem((item) => ({ ...item, itemUri: null }));
                setCurrentItemUri(null);
            }
        },
        [pickerType, handleFolderSelect, canSelectSite, sites]
    );
    /* istanbul ignore next */
    const handleClose = useCallback(() => {
        close(false);
    }, [close]);

    useModalPostMessageHandler(handleClose);

    const handleSelect = useCallback(() => {
        close(returnItem ? currentItem : currentItemUri);
    }, [close, currentItemUri]);

    const abortController = useAbortController();
    useEffect(() => {
        async function fetchData() {
            if (scopeBreadcrumb) {
                const bc = await fetchBreadcrumb(
                    itemUri,
                    abortController(true).signal
                );
                setCurrentItemUriLevels(bc.parts.length - 1);
            }
        }
        fetchData();
    }, [scopeBreadcrumb, itemUri, abortController]);

    const handleToggle = useCallback((isOpen, e) => {
        e.stopPropagation(); // HACK: prevent closing of Menu within Modal.Footer
    }, []);

    const [renderType, setRenderType] = useState(defaultRenderType);
    const renderedRenderType = useMemo(
        () => (
            <RenderType
                value={renderType}
                onChange={setRenderType}
                allowedRenderTypes={allowedRenderTypes}
            />
        ),
        [renderType, allowedRenderTypes]
    );
    const currentItemUriEnforced = currentRootItemUri.startsWith("pam-item://")
        ? currentRootItemUri
        : null;
    const disabled =
        currentItemUri == null ||
        (currentItemUri === itemUri && renderType !== PickerRenderType.Url);

    return (
        <Modal
            size={ModalSize.Small}
            fullscreen
            enforceFocus={false}
            show={open !== false}
            onHide={handleClose}
            dialogClassName={styles.picker}
            headerSize={0}
            footerSize={0}
        >
            <Modal.Body>
                {renderType == PickerRenderType.Search && (
                    <SearchPicker
                        searchForm={searchForm}
                        itemUri={currentItemUriEnforced}
                        onClose={handleClose}
                        onDocumentSelect={handleDocumentSelect}
                        onFolderSelect={handleFolderSelect}
                        topRowBefore={renderedRenderType}
                        filters={filters}
                    />
                )}
                {renderType == PickerRenderType.Target && (
                    <MiniPicker
                        toggles={toggles}
                        itemUri={currentItemUriEnforced}
                        onClose={handleClose}
                        onDocSingleClick={handleDocumentSelect}
                        onDocDoubleClick={(e, itemUri) =>
                            pickerType !== PickerType.Folder && close(itemUri)
                        }
                        onFolderSingleClick={handleFolderSelect}
                        onBreadcrumbPartClick={setRootItemUri}
                        breadcrumbScopeFromLevel={currentRootItemUriLevels}
                        canChangeSite={!scopeBreadcrumb && canChangeSite}
                        topRowBefore={renderedRenderType}
                    />
                )}
                {renderType == PickerRenderType.Url && (
                    <UrlPicker
                        itemUri={currentRootItemUri}
                        title={title}
                        caption={caption}
                        icon={icon}
                        isIconLast={isIconLast}
                        onClose={handleClose}
                        onItemUriSelect={setCurrentItemUri}
                        topRowBefore={renderedRenderType}
                    />
                )}
            </Modal.Body>
            <Modal.Footer>
                {helpText && (
                    <HelpBlock className="pull-left">{helpText}</HelpBlock>
                )}
                {!additionalActions?.length ? (
                    <Button
                        id="select-button"
                        aria-label="select"
                        disabled={disabled}
                        onClick={handleSelect}
                        bsStyle="primary"
                        className={styles.button}
                    >
                        {selectButton}
                    </Button>
                ) : (
                    <SplitButton
                        id="select-button"
                        aria-label="select"
                        disabled={disabled}
                        onToggle={handleToggle}
                        onClick={handleSelect}
                        bsStyle="primary"
                        title={selectButton}
                        className={styles.button}
                        pullRight
                        dropup
                    >
                        {additionalActions.map((action, i) => (
                            <MenuItem
                                eventKey={action.eventKey}
                                key={`pickermodal-menu-item-${i}`}
                                onSelect={() => {
                                    onSelect(action.eventKey);
                                    handleSelect();
                                }}
                            >
                                {action.label}
                            </MenuItem>
                        ))}
                    </SplitButton>
                )}
            </Modal.Footer>
        </Modal>
    );
};
PickerModal.displayName = "PickerModal";
export const openPickerModal = createModal(PickerModal);
